import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type ShellTheme = 'light' | 'dark';

type ShellPreferences = {
  theme: ShellTheme;
  toggleTheme: () => void;
  avatarUrl: string;
  setAvatarUrl: (url: string) => void;
};

const ShellPreferencesContext = createContext<ShellPreferences | null>(null);

const STORAGE_KEYS = {
  theme: 'fleet.shell.theme',
  avatar: 'fleet.shell.avatar',
};

function readStoredTheme(): ShellTheme {
  const stored = localStorage.getItem(STORAGE_KEYS.theme);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

type ShellPreferencesProviderProps = { children: ReactNode };

export function ShellPreferencesProvider({ children }: ShellPreferencesProviderProps) {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [avatarUrl, setAvatarUrlState] = useState(
    () => localStorage.getItem(STORAGE_KEYS.avatar) || ''
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem(STORAGE_KEYS.theme, theme);
    // Le pagine pubbliche (marketing, login) sono disegnate solo in chiaro.
    return () => document.documentElement.classList.remove('dark');
  }, [theme]);

  const toggleTheme = () => {
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
  };

  const setAvatarUrl = (url: string) => {
    setAvatarUrlState(url);
    localStorage.setItem(STORAGE_KEYS.avatar, url);
  };

  const value = useMemo<ShellPreferences>(
    () => ({
      theme,
      toggleTheme,
      avatarUrl,
      setAvatarUrl,
    }),
    [theme, avatarUrl]
  );

  return (
    <ShellPreferencesContext.Provider value={value}>{children}</ShellPreferencesContext.Provider>
  );
}

export function useShellPreferences(): ShellPreferences {
  const context = useContext(ShellPreferencesContext);
  if (!context) {
    throw new Error('useShellPreferences must be used within ShellPreferencesProvider');
  }
  return context;
}

/** Tema corrente; fuori dalla shell le pagine sono solo chiare. */
export function useShellTheme(): ShellTheme {
  return useContext(ShellPreferencesContext)?.theme ?? 'light';
}
